import React, { useEffect, useRef, useState } from "react";
import clsx from "clsx";
import useBreakpoint from "../../../../Grid/hooks/useBreakpoint";
import { ILandingInnovationProductAdrienCachot } from "../LandingInnovationAdrienCachot";

import Typography from "../../../../../Foundation/Typography/Typography";
import FlexBox from "../../../../FlexBox/FlexBox";
import { _InnovationProductAdrienCachotRoot } from "./InnovationProductAdrienCachot.styled";
import htmlParse from "html-react-parser";
import StyledDiv from "../../../../StyledDiv/StyledDiv";
import { useNotifyInnovationDesktopVideoPlaying } from "../InnovationVideoPlayingContext";
import { GradientBaseButton } from "../../../../../Foundation/Button/GradientButton.styled";
import useOnScreen from "../../../../../Hooks/useOnScreen";

export interface IInnovationProductAdrienCachotProps<T extends any = any> {
  product: ILandingInnovationProductAdrienCachot<T>;
  className?: string;
  onClick?: (product: ILandingInnovationProductAdrienCachot) => void;
  showLessLabel: string;
  showMoreLabel: string;
  ctaLabel?: string;
  hideCta?: boolean;
  renderVideo?: (
    videoSlug: string,
    videoRef: React.MutableRefObject<HTMLVideoElement | null>,
    props: React.VideoHTMLAttributes<HTMLVideoElement>
  ) => React.ReactNode;
}

const InnovationProductAdrienCachot = <T extends any = any>(
  props: IInnovationProductAdrienCachotProps<T>
) => {
  const {
    product,
    className,
    onClick,
    showLessLabel,
    showMoreLabel,
    ctaLabel: _ctaLabel,
    hideCta: hideCtaProp,
    renderVideo,
  } = props;
  const videoSlug = product.videoSlug;
  const { mobile } = useBreakpoint();
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const videoAreaRef = useRef<HTMLDivElement | null>(null);
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const rafIdRef = useRef<number | null>(null);
  const previousVideoDisplayRef = useRef<string>("");
  const [isVisible, setIsVisible] = useState(false);
  /** Poster vidéo (fond) : URL chargée à l’approche du viewport */
  const [productRef, shouldLoadPoster] = useOnScreen<HTMLDivElement>(
    { root: null, rootMargin: "200px 0px", threshold: 0 },
    { ignoreReverse: true }
  );
  const notifyDesktopVideoPlaying = useNotifyInnovationDesktopVideoPlaying();

  // Masque les calques mix-blend (rose / bleu) pendant la lecture vidéo desktop
  useEffect(() => {
    if (mobile || !notifyDesktopVideoPlaying) return;
    if (!isVisible) return;
    notifyDesktopVideoPlaying(1);
    return () => {
      notifyDesktopVideoPlaying(-1);
    };
  }, [isVisible, mobile, notifyDesktopVideoPlaying]);

  const ctaLabel = _ctaLabel || product.ctaLabel || "Je commande";
  const hideCta =
    hideCtaProp === true ||
    Boolean((product as { hideCta?: boolean }).hideCta);

  // const [showMore, setShowMore] = useState<boolean>(false);
  // const [isClamped, setClamped] = useState(false);

  // const handleShowMore = () => {
  //   setShowMore(!showMore);
  // };

  // Auto-play quand l'élément est dans le viewport (mobile)
  useEffect(() => {
    let cleanupGesture: (() => void) | null = null;

    const tryPlay = (video: HTMLVideoElement) => {
      // S'assure que la vidéo est chargée (preload="none" dans SushishopVideo)
      const doPlay = () => {
        const promise = video.play();
        if (promise === undefined) return;
        promise.catch(() => {
          // iOS bloque play() hors gesture utilisateur.
          // touchmove couvre le cas où l'utilisateur est en train de scroller
          // (quasi-immédiat), touchstart couvre le cas où il s'est arrêté.
          let handled = false;
          const handleGesture = () => {
            if (handled) return;
            handled = true;
            document.removeEventListener("touchstart", handleGesture);
            document.removeEventListener("touchmove", handleGesture);
            cleanupGesture = null;
            video.play().catch(() => {});
          };
          document.addEventListener("touchstart", handleGesture, { passive: true });
          document.addEventListener("touchmove", handleGesture, { passive: true });
          cleanupGesture = () => {
            document.removeEventListener("touchstart", handleGesture);
            document.removeEventListener("touchmove", handleGesture);
          };
        });
      };

      if (video.readyState === 0) {
        video.load();
        video.addEventListener("canplay", doPlay, { once: true });
      } else {
        doPlay();
      }
    };

    const observer = new IntersectionObserver(
      (entries) => {
        if (!mobile) return;
        entries.forEach((entry) => {
          const video = videoRef.current;
          if (entry.isIntersecting) {
            setIsVisible(true);
            if (video) tryPlay(video);
          } else {
            setIsVisible(false);
            cleanupGesture?.();
            cleanupGesture = null;
            video?.pause();
          }
        });
      },
      { threshold: 0.8 }
    );

    if (productRef.current && mobile) {
      observer.observe(productRef.current);
    }

    return () => {
      cleanupGesture?.();
      observer.disconnect();
    };
  }, [mobile]);

  const defaultVideoRenderer = () => {
    if (!videoSlug) return null;

    return (
      <video
        ref={videoRef}
        muted
        loop
        playsInline
        autoPlay={isVisible}
        src={videoSlug}
        style={{ width: "100%", height: "100%", objectFit: "cover" }}
      />
    );
  };

  // Rendu "video -> canvas" (découpe en aspect cover + drawImage en boucle)
  useEffect(() => {
    // Certains slide montent avec un ref vidéo pas encore prêt.
    // On "poll" au prochain rAF jusqu’à avoir container + video.
    let cancelled = false;
    let initRaf: number | null = null;
    let initialized = false;

    let detachVideoListeners = () => {};
    let disconnectResizeObserver = () => {};

    const stop = () => {
      if (rafIdRef.current !== null) {
        cancelAnimationFrame(rafIdRef.current);
        rafIdRef.current = null;
      }
    };

    const init = () => {
      if (cancelled || initialized) return;

      const container = videoAreaRef.current;
      const video = videoRef.current;
      if (!container || !video) {
        initRaf = requestAnimationFrame(init);
        return;
      }

      const sushishopWrapper =
        video.closest<HTMLElement>(".sushishop-video__wrapper");
      const drawRoot = sushishopWrapper || container;

      const ctx = (() => {
        // Création canvas une seule fois
        if (!canvasRef.current) {
          const existingCanvas = drawRoot.querySelector<HTMLCanvasElement>(
            "canvas[data-innovation-video-canvas='true']"
          );
          if (existingCanvas) {
            canvasRef.current = existingCanvas;
          } else {
            const c = document.createElement("canvas");
            c.dataset.innovationVideoCanvas = "true";

            // Remplit le conteneur et reste derrière le contenu/button
            c.style.position = "absolute";
            c.style.inset = "0";
            c.style.width = "100%";
            c.style.height = "100%";
            c.style.zIndex = "0";
            c.style.pointerEvents = "none";

            if (drawRoot === container) {
              container.insertBefore(c, container.firstChild);
            } else {
              // Pour SushishopVideo, poster = z-index 1
              drawRoot.appendChild(c);
            }
            canvasRef.current = c;
          }
        }

        return canvasRef.current?.getContext("2d") || null;
      })();

      if (!ctx || !canvasRef.current) return;

      const dpr = typeof window !== "undefined" ? window.devicePixelRatio || 1 : 1;
      const size = { cw: 0, ch: 0 };

      const updateSize = () => {
        const rect = container.getBoundingClientRect();
        const cw = rect.width;
        const ch = rect.height;
        if (cw <= 0 || ch <= 0) return;
        if (size.cw === cw && size.ch === ch) return;

        size.cw = cw;
        size.ch = ch;

        const c = canvasRef.current!;
        const pixelW = Math.max(1, Math.floor(cw * dpr));
        const pixelH = Math.max(1, Math.floor(ch * dpr));
        c.width = pixelW;
        c.height = pixelH;

        // On travaille en "CSS pixels" dans drawImage.
        ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      };

      // Réduction du jank : on évite width/height à chaque frame.
      if (typeof ResizeObserver !== "undefined") {
        const ro = new ResizeObserver(() => updateSize());
        ro.observe(container);
        disconnectResizeObserver = () => ro.disconnect();
      } else if (typeof window !== "undefined") {
        const onResize = () => updateSize();
        window.addEventListener("resize", onResize);
        disconnectResizeObserver = () =>
          window.removeEventListener("resize", onResize);
      }

      updateSize();

      const draw = () => {
        if (!canvasRef.current) return;
        const cw = size.cw;
        const ch = size.ch;
        if (cw <= 0 || ch <= 0) return;

        const vw = video.videoWidth || cw;
        const vh = video.videoHeight || ch;
        if (vw <= 0 || vh <= 0) return;

        const vr = vw / vh;
        const cr = cw / ch;

        let sx = 0;
        let sy = 0;
        let sw = vw;
        let sh = vh;

        // Logique "cover" sans déformation (crop des bords)
        if (vr > cr) {
          sw = vh * cr;
          sx = (vw - sw) / 2;
        } else {
          sh = vw / cr;
          sy = (vh - sh) / 2;
        }

        ctx.drawImage(video, sx, sy, sw, sh, 0, 0, cw, ch);
      };

      const startLoop = () => {
        stop();

        const loop = () => {
          if (video.paused || video.ended) {
            stop();
            return;
          }
          draw();
          rafIdRef.current = requestAnimationFrame(loop);
        };

        draw(); // First paint
        rafIdRef.current = requestAnimationFrame(loop);
      };

      const start = () => {
        // Garantit que videoWidth/videoHeight sont disponibles rapidement.
        if (video.readyState >= 2) {
          startLoop();
          return;
        }

        const onLoaded = () => {
          video.removeEventListener("loadeddata", onLoaded);
          video.removeEventListener("loadedmetadata", onLoaded);
          startLoop();
        };

        video.addEventListener("loadeddata", onLoaded, { once: true });
        video.addEventListener("loadedmetadata", onLoaded, { once: true });
      };

      // Masque la balise <video> : le canvas prend le relais.
      previousVideoDisplayRef.current = video.style.display;
      video.style.display = "none";

      const handlePlay = () => start();
      const handleStop = () => stop();

      video.addEventListener("play", handlePlay);
      video.addEventListener("pause", handleStop);
      video.addEventListener("ended", handleStop);

      // Au cas où la vidéo est déjà en train de jouer (hover / autoplay)
      if (!video.paused) start();

      detachVideoListeners = () => {
        video.removeEventListener("play", handlePlay);
        video.removeEventListener("pause", handleStop);
        video.removeEventListener("ended", handleStop);
      };

      initialized = true;
    };

    init();

    return () => {
      cancelled = true;
      if (initRaf !== null) cancelAnimationFrame(initRaf);
      stop();
      detachVideoListeners();
      disconnectResizeObserver();

      // On restaure l'affichage de la vidéo
      const video = videoRef.current;
      if (video) video.style.display = previousVideoDisplayRef.current;
    };
  }, [videoSlug]);

  const renderButton =
    !hideCta && (
      <GradientBaseButton
        className={clsx("innovation-adrien-cachot-product-action-button")}
        onClick={() => onClick?.(product)}
      >
        {ctaLabel}
      </GradientBaseButton>
    );

  return (
    <_InnovationProductAdrienCachotRoot
      direction="column"
      gap={0}
      className={clsx(className, { "action-on-hover": "" })}
      onMouseEnter={() => {
        if (!mobile) {
          setIsVisible(true);
          const video = videoRef.current;
          if (video) {
            video.play().catch(() => {
              // La vidéo n'est pas encore chargée, on force le load puis play
              video.load();
              video.addEventListener(
                "canplay",
                () => video.play().catch(() => {}),
                {
                  once: true,
                }
              );
            });
          }
        }
      }}
      onMouseLeave={() => {
        if (!mobile) {
          setIsVisible(false);
          videoRef.current?.pause();
        }
      }}
      ref={productRef}
    >
      <div
        ref={videoAreaRef}
        className="innovation-adrien-cachot-product-video"
      >
        {renderVideo
          ? renderVideo(videoSlug, videoRef, {
              muted: true,
              loop: true,
              playsInline: true,
              autoPlay: isVisible,
              poster:
                !shouldLoadPoster
                  ? undefined
                  : typeof product.poster === "string"
                    ? product.poster
                    : mobile
                      ? product.poster?.mobile
                      : product.poster?.desktop,
            })
          : defaultVideoRenderer()}

        {/* Button desktop */}
        <StyledDiv
          styles={{
            padding: 24,
            position: "absolute",
            bottom: 0,
            left: 0,
            mobile: {
              display: "none",
            },
          }}
        >
          <div>{renderButton}</div>
        </StyledDiv>
      </div>

      <FlexBox
        className="innovation-adrien-cachot-product-content"
        direction="column"
        align="flex-start"
        gap={12}
      >
        <StyledDiv
          as={Typography}
          styles={{
            maxWidth: 232,
            mobile: {
              maxWidth: 265,
            },
          }}
          variant={"adriencachot4"}
          color="white"
        >
          {product.title}
        </StyledDiv>

        <StyledDiv
          as={Typography}
          styles={{
            maxWidth: 232,
            mobile: {
              maxWidth: 265,
            },
          }}
          variant="adriencachotBody2"
          color="white"
          // ellipse={!showMore}
          // ellipseLine={3}
          // showSeeMore={true}
          // onClamped={setClamped}
        >
          {typeof product.description === "string"
            ? htmlParse(product.description)
            : product.description}
        </StyledDiv>

        {/* {isClamped && (
          <ShowMoreLink
            icon={showMore ? "minus" : "plus"}
            label={showMore ? showLessLabel : showMoreLabel}
            color={highlightedColorContext}
            onClick={handleShowMore}
          />
        )} */}

        <FlexBox
          wrap={"wrap"}
          gap={10}
          style={{
            marginTop: 12,
          }}
        >
          <Typography
            variant={"adriencachotBody2"}
            useContextColor={true}
            color="white"
          >
            {product.price}
          </Typography>
          {product.priceSubtitle && (
            <>
              <Typography variant="adriencachotBody2" color="white">
                {"/"} {product.priceSubtitle}
              </Typography>
            </>
          )}
        </FlexBox>

        {/* Button */}
        <StyledDiv
          styles={{
            display: "none",
            marginTop: "auto",
            mobile: {
              display: "block",
            },
          }}
        >
          {renderButton}
        </StyledDiv>
      </FlexBox>
    </_InnovationProductAdrienCachotRoot>
  );
};

export default InnovationProductAdrienCachot;
